import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { lastValueFrom, RmsMessageService, PromptService, CustomerCrmService } from '@services';
import { RmsModule } from '@rms';
import { take } from 'rxjs/operators';
import { RmsPulldownRef } from '../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';
import { PaymentsDataService, PaymentOptions } from '../../../services/payments-data.service';

// =====================================================================
// ReceiptDetailComponent — receipt pulldown (Receipts / Exceptions / Needs receipt tabs)
// ---------------------------------------------------------------------
// One receipt = one row in dbo.payment (money received from a customer).
//   Receipt    edit details (unposted only), Mark Processing, Post, Return (posted only)
//   Allocate   split the receipt across the customer's open orders; leftover = unapplied cash
//   Follow-up  exception code, note, owner, next action; add a note
//   History    ledger rows, related receipts (return / original), action history
// Every write sends the RowVer it loaded; if someone else saved first → 409 → we reload.
// Closes with true when anything was saved (the caller refreshes its list).
// New receipt: open with ChargeLogID = null (customer + amount + bank required).
// =====================================================================

export interface ReceiptDetailData {
  ChargeLogID: number | null;
  Options: PaymentOptions | null;
  CompanyID?: number | null;     // new receipt: preselect the customer
  OrderID?: number | null;       // new receipt: preselect the order
}

interface ReceiptForm {
  CompanyID: number | null;
  OrderID: string;               // text box; '' = none
  PaymentDetailTypeID: number | null;
  BankID: number | null;
  Amount: number | null;
  CustomerFeeAmount: number | null;
  ProcessorFeeAmount: number | null;
  ReceivedDate: string | null;
  DepositDate: string | null;
  CheckNumber: string;
  MaskedReference: string;
  ProcessorName: string;
  ProcessorReference: string;
  Notes: string;
  CurrencyCode: string;
}

interface AllocationLine {
  OrderID: number;
  SaleDate: string | null;
  OrderStatusName: string | null;
  OrderTotal: number;
  OpenBeforeThisReceipt: number;
  AppliedFromThisReceipt: number;
  BalanceDue: number;
  Apply: number | null;
}

@Component({
  selector: 'app-receipt-detail',
  imports: [RmsModule],
  templateUrl: './receipt-detail.component.html',
  styleUrl: './receipt-detail.component.scss'
})
export class ReceiptDetailComponent implements OnInit {
  ChargeLogID: number | null;
  Ready = false;                 // first load done (reloads keep the tabs on screen, so the open tab stays)
  Loading = true;
  Busy = false;
  Changed = false;
  Error = '';

  Receipt: any = null;
  Allocations: any[] = [];       // allocation history (all statuses)
  Ledger: any[] = [];
  History: any[] = [];
  Related: any[] = [];

  Form: ReceiptForm = this.emptyForm();
  Lines: AllocationLine[] = [];

  // Follow-up
  ExceptionCode = '';
  ExceptionNote = '';
  NextAction = '';
  OwnerChoice = '';              // '' nobody | GUID
  NoteText = '';

  // Braintree status lookup (7.2.28)
  BtLoading = false;
  BtError = '';
  BtInfo: { Environment: string; Transaction: any; Match: any } | null = null;

  // Return
  ReturnReason = '';
  ReturnDate: string = PaymentsDataService.today();

  // Lists
  Customers: { CompanyID: number; CompanyName: string; Label: string }[] = [];
  ChangingCustomer = false;      // "Change" clicked on a saved, unposted receipt
  Banks: { BankID: number; BankName: string }[] = [];
  DetailTypes: { PaymentDetailTypeID: number; DetailTypeName: string }[] = [];
  ExceptionOptions: { value: string; label: string }[] = [];
  OwnerOptions: { value: string; label: string }[] = [];

  constructor(
    @Inject(RMS_PULLDOWN_DATA) public data: ReceiptDetailData,
    public ref: RmsPulldownRef,
    private paymentsData: PaymentsDataService,
    private customerCrm: CustomerCrmService,
    private prompt: PromptService,
    private msg: RmsMessageService,
    private cdr: ChangeDetectorRef
  ) {
    this.ChargeLogID = data.ChargeLogID ?? null;
    const opts = data.Options;
    this.Banks = (opts?.Banks ?? []).filter(b => b.IsActive);
    // money received only — store-credit / adjustment types never post, so a new receipt with one lands in Exceptions
    this.DetailTypes = (opts?.DetailTypes ?? []).filter(d => d.IsActive && !d.IsWithdrawal && d.PostsToLedger);
    this.ExceptionOptions = [
      { value: '', label: 'No exception' },
      ...(opts?.ExceptionCodes ?? []).filter(e => !e.IsSystem).map(e => ({ value: e.ExceptionCode, label: e.ExceptionName }))
    ];
  }

  async ngOnInit() {
    if (this.IsNew) {
      this.Form.CompanyID = this.data.CompanyID ?? null;
      this.Form.OrderID = this.data.OrderID ? String(this.data.OrderID) : '';
      this.Form.BankID = this.Banks[0]?.BankID ?? null;
      this.Form.ReceivedDate = PaymentsDataService.today();
      await this.ensureCustomers();
      this.Loading = false;
      this.Ready = true;
      this.cdr.markForCheck();
      return;
    }
    await this.loadAll();
  }

  // ---------------------------------------------------------------------
  // State helpers
  // ---------------------------------------------------------------------

  get IsNew(): boolean { return !this.ChargeLogID; }
  get Stage(): string { return this.Receipt?.Stage ?? 'NEW'; }
  get IsPosted(): boolean { return this.Stage === 'POSTED'; }
  get IsReturn(): boolean { return !!this.Receipt?.ReversalOfChargeLogID; }
  get WasReturned(): boolean { return !!this.Receipt?.ReversedByChargeLogID; }

  get CanEdit(): boolean { return this.IsNew || (!this.IsPosted && !this.IsReturn); }
  /** Customer can be picked on a new receipt, on an unposted one that has none (old legacy rows),
   *  or after "Change" on an unposted one with no allocations (the proc refuses a change once allocated) */
  get CanChooseCustomer(): boolean {
    return this.CanEdit && (this.IsNew || !this.Receipt?.CompanyID || this.ChangingCustomer);
  }
  get CanChangeCustomer(): boolean {
    return this.CanEdit && !this.IsNew && !!this.Receipt?.CompanyID && !this.ChangingCustomer
      && (this.Receipt?.AllocationCount ?? 0) === 0;
  }

  async startChangeCustomer() {
    this.ChangingCustomer = true;
    await this.ensureCustomers();
    this.cdr.markForCheck();
  }
  get CanMarkProcessing(): boolean { return !this.IsNew && this.Stage === 'ENTERED' && !this.IsReturn; }
  get CanPost(): boolean {
    return !this.IsNew && !this.IsPosted && !this.IsReturn && !this.Receipt?.ExceptionCode && (this.Receipt?.GrossAmount ?? 0) > 0;
  }
  get CanReturn(): boolean {
    return !this.IsNew && this.IsPosted && !this.IsReturn && !this.WasReturned && (this.Receipt?.GrossAmount ?? 0) > 0;
  }
  get CanAllocate(): boolean {
    return !this.IsNew && !this.IsReturn && !this.WasReturned && !this.Receipt?.IsWithdrawal && !!this.Receipt?.CompanyID
      && (this.Receipt?.ApplicableAmount ?? 0) > 0;
  }

  /** Net deposit shown live while typing (amount − processor fee) */
  get NetDeposit(): number {
    return Math.round(((Number(this.Form.Amount) || 0) - (Number(this.Form.ProcessorFeeAmount) || 0)) * 100) / 100;
  }

  // Allocate tab totals
  get Available(): number { return Number(this.Receipt?.ApplicableAmount ?? 0); }
  get Allocated(): number {
    return Math.round(this.Lines.reduce((s, l) => s + (Number(l.Apply) || 0), 0) * 100) / 100;
  }
  get Unapplied(): number { return Math.round((this.Available - this.Allocated) * 100) / 100; }
  lineError(l: AllocationLine): string {
    const a = Number(l.Apply) || 0;
    if (a < 0) return 'Cannot be negative';
    if (a > l.OpenBeforeThisReceipt + 0.005) return `Only ${l.OpenBeforeThisReceipt.toFixed(2)} open`;
    return '';
  }
  get AllocationValid(): boolean {
    return this.Unapplied >= -0.005 && this.Lines.every(l => !this.lineError(l));
  }

  private emptyForm(): ReceiptForm {
    return {
      CompanyID: null, OrderID: '', PaymentDetailTypeID: null, BankID: null,
      Amount: null, CustomerFeeAmount: null, ProcessorFeeAmount: null,
      ReceivedDate: null, DepositDate: null, CheckNumber: '', MaskedReference: '',
      ProcessorName: '', ProcessorReference: '', Notes: '', CurrencyCode: 'USD'
    };
  }

  private errText(err: any, fallback: string): string {
    return err?.error?.error ?? err?.message ?? fallback;
  }

  // ---------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------

  async loadAll() {
    if (!this.ChargeLogID) return;
    this.Loading = true;
    this.Error = '';
    try {
      const d = await lastValueFrom(this.paymentsData.getReceipt(this.ChargeLogID));
      this.Receipt = d?.Receipt ?? null;
      this.Allocations = d?.Allocations ?? [];
      this.Ledger = d?.Ledger ?? [];
      this.History = d?.History ?? [];
      this.Related = d?.Related ?? [];
      this.ChangingCustomer = false;
      this.fillForm();
      if (this.CanChooseCustomer) await this.ensureCustomers();

      this.Lines = [];
      if (this.CanAllocate) {
        const opts = await lastValueFrom(this.paymentsData.getAllocationOptions(this.ChargeLogID));
        this.Lines = (opts ?? []).map((o: any) => ({
          OrderID: o.OrderID,
          SaleDate: o.SaleDate,
          OrderStatusName: o.OrderStatusName,
          OrderTotal: Number(o.OrderTotal) || 0,
          OpenBeforeThisReceipt: Math.max(0, Number(o.OpenBeforeThisReceipt) || 0),
          AppliedFromThisReceipt: Number(o.AppliedFromThisReceipt) || 0,
          BalanceDue: Number(o.BalanceDue) || 0,
          Apply: Number(o.AppliedFromThisReceipt) || null
        }));
      }
    } catch (err: any) {
      this.Error = this.errText(err, 'Could not load the receipt.');
    } finally {
      this.Loading = false;
      this.Ready = true;
      this.cdr.markForCheck();
    }
  }

  /** Active customers for the picker (loaded once) */
  private async ensureCustomers() {
    if (this.Customers.length) return;
    try {
      const rows = await lastValueFrom(this.customerCrm.searchCustomers('', false));
      // show the Cust # too (same as the Order Payment Status grid) — several customers have near-identical names
      this.Customers = (rows ?? []).map((c: any) => ({ CompanyID: c.CompanyID, CompanyName: c.CompanyName, Label: `${c.CompanyName} · C-${c.CompanyID}` }));
    } catch {
      this.msg.error('Could not load the customer list.');
    }
  }

  private fillForm() {
    const r = this.Receipt;
    if (!r) return;
    this.Form = {
      CompanyID: r.CompanyID ?? null,
      OrderID: r.OrderID ? String(r.OrderID) : '',
      PaymentDetailTypeID: r.PaymentDetailTypeID ?? null,
      BankID: r.BankID ?? null,
      Amount: r.GrossAmount ?? null,
      CustomerFeeAmount: r.CustomerFeeAmount ?? null,
      ProcessorFeeAmount: r.ProcessorFeeAmount ?? null,
      ReceivedDate: PaymentsDataService.dateOnly(r.ReceivedDate),
      DepositDate: PaymentsDataService.dateOnly(r.DepositDate),
      CheckNumber: r.CheckNumber ?? '',
      MaskedReference: r.MaskedReference ?? '',
      ProcessorName: r.ProcessorName ?? '',
      ProcessorReference: r.ProcessorReference ?? '',
      Notes: r.Notes ?? '',
      CurrencyCode: r.CurrencyCode ?? 'USD'
    };
    if (!this.Form.BankID && !this.IsPosted) this.Form.BankID = this.Banks[0]?.BankID ?? null;

    // an older receipt may use a type that is no longer offered — keep it in the list so the field still shows it
    if (r.PaymentDetailTypeID && !this.DetailTypes.some(d => d.PaymentDetailTypeID === r.PaymentDetailTypeID)) {
      this.DetailTypes = [...this.DetailTypes, { PaymentDetailTypeID: r.PaymentDetailTypeID, DetailTypeName: r.DetailTypeName ?? `Type ${r.PaymentDetailTypeID}` }];
    }

    this.ExceptionCode = r.ExceptionCode ?? '';
    this.ExceptionNote = r.ExceptionNote ?? '';
    this.NextAction = r.NextActionOverride ?? '';
    const me = this.paymentsData.currentUserGUID;
    this.OwnerChoice = r.OwnerGUID ?? '';
    this.OwnerOptions = [{ value: '', label: 'Nobody' }];
    if (me) this.OwnerOptions.push({ value: me, label: 'Me' });
    if (r.OwnerGUID && r.OwnerGUID.toLowerCase() !== (me ?? '').toLowerCase()) {
      this.OwnerOptions.push({ value: r.OwnerGUID, label: r.OwnerName ?? 'Current owner' });
    }
    if (me && r.OwnerGUID && r.OwnerGUID.toLowerCase() === me.toLowerCase()) this.OwnerChoice = me;
  }

  // ---------------------------------------------------------------------
  // Receipt tab
  // ---------------------------------------------------------------------

  private formError(): string {
    const f = this.Form;
    if (!f.CompanyID) return 'Choose the customer.';
    if (!f.PaymentDetailTypeID) return 'Choose the payment type.';
    if (!f.BankID) return 'Choose the bank.';
    if (!(Number(f.Amount) > 0)) return 'Amount must be greater than 0.';
    if (f.OrderID && !/^\d+$/.test(String(f.OrderID).trim())) return 'Order # must be a number.';
    if ((Number(f.CustomerFeeAmount) || 0) < 0 || (Number(f.ProcessorFeeAmount) || 0) < 0) return 'Fees cannot be negative.';
    if ((Number(f.ProcessorFeeAmount) || 0) >= Number(f.Amount)) return 'The processor fee must be smaller than the amount.';
    if ((Number(f.CustomerFeeAmount) || 0) >= Number(f.Amount)) return 'The customer fee must be smaller than the amount.';
    return '';
  }

  private buildPayload(): any {
    const f = this.Form;
    const money = (v: any) => (v === null || v === undefined || String(v) === '' ? null : Math.round(Number(v) * 100) / 100);
    return {
      ChargeLogID: this.ChargeLogID,
      RowVer: this.Receipt?.RowVer ?? null,
      CompanyID: f.CompanyID,
      OrderID: f.OrderID ? parseInt(String(f.OrderID).trim(), 10) : null,
      PaymentDetailTypeID: f.PaymentDetailTypeID,
      BankID: f.BankID,
      Amount: money(f.Amount),
      CustomerFeeAmount: money(f.CustomerFeeAmount),
      ProcessorFeeAmount: money(f.ProcessorFeeAmount),
      NetDepositAmount: f.ProcessorFeeAmount ? this.NetDeposit : null,
      ReceivedDate: f.ReceivedDate,
      DepositDate: f.DepositDate,
      CheckNumber: f.CheckNumber,
      MaskedReference: f.MaskedReference,
      ProcessorName: f.ProcessorName,
      ProcessorReference: f.ProcessorReference,
      Notes: f.Notes,
      CurrencyCode: f.CurrencyCode || 'USD'
    };
  }

  /** Save receipt details. Returns false if it failed (message already shown). */
  private async saveDetails(): Promise<boolean> {
    const problem = this.formError();
    if (problem) { this.Error = problem; return false; }
    const result = await lastValueFrom(this.paymentsData.saveReceipt(this.buildPayload()));
    this.Changed = true;
    if (this.IsNew) {
      this.ChargeLogID = result?.ChargeLogID ?? null;
      if (result?.AlreadyExisted) this.msg.info(`That processor reference is already receipt #${this.ChargeLogID} — opened it.`);
    }
    if (this.Receipt && result?.RowVer !== undefined) this.Receipt.RowVer = result.RowVer;
    return true;
  }

  async save() {
    if (this.Busy) return;
    this.Busy = true;
    this.Error = '';
    try {
      const wasNew = this.IsNew;
      if (await this.saveDetails()) {
        this.msg.success(wasNew ? `Receipt #${this.ChargeLogID} created.` : `Receipt #${this.ChargeLogID} saved.`);
        await this.loadAll();
      }
    } catch (err: any) {
      await this.handleWriteError(err, 'The receipt could not be saved.');
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  async markProcessing() {
    if (this.Busy || !this.CanMarkProcessing) return;
    this.Busy = true;
    this.Error = '';
    try {
      if (!(await this.saveDetails())) return;
      await lastValueFrom(this.paymentsData.markProcessing(this.ChargeLogID!, this.Receipt?.RowVer ?? null));
      this.Changed = true;
      this.msg.success(`Receipt #${this.ChargeLogID} is now Processing.`);
      await this.loadAll();
    } catch (err: any) {
      await this.handleWriteError(err, 'Could not mark the receipt as processing.');
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  async post() {
    if (this.Busy || !this.CanPost) return;
    if (!this.Form.DepositDate) { this.Error = 'Enter the bank deposit date before posting.'; return; }
    if (this.Form.DepositDate > PaymentsDataService.today()) { this.Error = 'Deposit date cannot be in the future.'; return; }
    if (!this.Form.BankID) { this.Error = 'Choose the bank the money was deposited into.'; return; }

    this.Busy = true;
    this.Error = '';
    try {
      if (!(await this.saveDetails())) return;      // keep any edits made on the form
      const r = await lastValueFrom(this.paymentsData.postReceipt(
        this.ChargeLogID!, this.Receipt?.RowVer ?? null, this.Form.DepositDate, this.Form.BankID));
      this.Changed = true;
      this.msg.success(r?.AlreadyPosted
        ? `Receipt #${this.ChargeLogID} was already posted.`
        : `Receipt #${this.ChargeLogID} posted to the ledger.`);
      await this.loadAll();
    } catch (err: any) {
      await this.handleWriteError(err, 'The receipt could not be posted.');
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  returnPayment() {
    if (this.Busy || !this.CanReturn) return;
    if (!this.ReturnReason.trim()) { this.Error = 'Enter the reason for the return.'; return; }
    const amount = Number(this.Receipt?.GrossAmount ?? 0).toFixed(2);
    const { closed$ } = this.prompt.open({
      promptText: `Return receipt #${this.ChargeLogID} (${amount})? This creates a reversing entry and puts the money back on the orders. It cannot be undone.`,
      buttons: [{ label: 'Return payment', buttonClass: 'btn-bg-red', value: true }]
    });
    closed$.pipe(take(1)).subscribe(async (ok: any) => {
      if (!ok) return;
      this.Busy = true;
      this.Error = '';
      try {
        const r = await lastValueFrom(this.paymentsData.returnReceipt(
          this.ChargeLogID!, this.Receipt?.RowVer ?? null, this.ReturnReason.trim(), this.ReturnDate));
        this.Changed = true;
        this.ReturnReason = '';
        this.msg.success(`Receipt #${this.ChargeLogID} returned (return receipt #${r?.ReturnChargeLogID}).`);
        await this.loadAll();
      } catch (err: any) {
        await this.handleWriteError(err, 'The payment could not be returned.');
      } finally {
        this.Busy = false;
        this.cdr.markForCheck();
      }
    });
  }

  // ---------------------------------------------------------------------
  // Allocate tab
  // ---------------------------------------------------------------------

  /** Oldest order first, up to what is open on each order and what the receipt has left */
  fillOldestFirst() {
    let left = this.Available;
    const oldestFirst = [...this.Lines].sort((a, b) =>
      String(a.SaleDate ?? '').localeCompare(String(b.SaleDate ?? '')) || a.OrderID - b.OrderID);
    for (const l of oldestFirst) {
      const amt = Math.max(0, Math.min(l.OpenBeforeThisReceipt, left));
      l.Apply = amt > 0 ? Math.round(amt * 100) / 100 : null;
      left = Math.round((left - amt) * 100) / 100;
    }
  }

  clearAllocations() {
    this.Lines.forEach(l => l.Apply = null);
  }

  async saveAllocations() {
    if (this.Busy || !this.CanAllocate) return;
    if (!this.AllocationValid) { this.Error = 'Fix the amounts in red first.'; return; }
    this.Busy = true;
    this.Error = '';
    try {
      const list = this.Lines
        .filter(l => (Number(l.Apply) || 0) > 0 || l.AppliedFromThisReceipt > 0)   // 0 on an existing one = cancel it
        .map(l => ({ OrderID: l.OrderID, Amount: Math.round((Number(l.Apply) || 0) * 100) / 100 }));
      const r = await lastValueFrom(this.paymentsData.saveAllocations(this.ChargeLogID!, this.Receipt?.RowVer ?? null, list));
      this.Changed = true;
      this.msg.success(`Allocations saved. Unapplied: ${Number(r?.UnappliedAmount ?? 0).toFixed(2)}.`);
      await this.loadAll();
    } catch (err: any) {
      await this.handleWriteError(err, 'The allocations could not be saved.');
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  // ---------------------------------------------------------------------
  // Follow-up tab
  // ---------------------------------------------------------------------

  async saveFollowup() {
    if (this.Busy || this.IsNew) return;
    this.Busy = true;
    this.Error = '';
    try {
      await lastValueFrom(this.paymentsData.setFollowup({
        ChargeLogID: this.ChargeLogID!,
        RowVer: this.Receipt?.RowVer ?? null,
        ExceptionCode: this.ExceptionCode || null,
        ExceptionNote: this.ExceptionNote || null,
        OwnerGUID: this.OwnerChoice || null,
        NextAction: this.NextAction || null
      }));
      this.Changed = true;
      this.msg.success('Follow-up saved.');
      await this.loadAll();
    } catch (err: any) {
      await this.handleWriteError(err, 'The follow-up could not be saved.');
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  async addNote() {
    const text = this.NoteText.trim();
    if (this.Busy || this.IsNew || !text) return;
    this.Busy = true;
    this.Error = '';
    try {
      await lastValueFrom(this.paymentsData.addNote(this.ChargeLogID!, text));
      this.Changed = true;
      this.NoteText = '';
      this.msg.success('Note added.');
      await this.loadAll();
    } catch (err: any) {
      await this.handleWriteError(err, 'The note could not be added.');
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  // ---------------------------------------------------------------------
  // Errors / close
  // ---------------------------------------------------------------------

  /** 409 = someone else saved this receipt → reload so the user sees the latest */
  private async handleWriteError(err: any, fallback: string) {
    this.Error = this.errText(err, fallback);
    if (err?.status === 409 || err?.error?.code === 50709) {
      this.msg.warn('Someone else changed this receipt. It has been reloaded — check it and try again.');
      await this.loadAll();
      this.Error = this.errText(err, fallback);
    }
  }

  // ---------------------------------------------------------------------
  // Braintree status lookup (7.2.28)
  // ---------------------------------------------------------------------

  /** Braintree ID saved on this receipt (only Braintree receipts have one) */
  get BraintreeId(): string | null {
    const ref = (this.Receipt?.ProcessorReference ?? '').toString().trim();
    const isBt = !this.Receipt?.ProcessorName || /braintree/i.test(this.Receipt.ProcessorName);
    return ref && isBt && /^[A-Za-z0-9_-]{1,64}$/.test(ref) ? ref : null;
  }

  async checkBraintree() {
    const id = this.BraintreeId;
    if (!id || this.BtLoading) return;
    this.BtLoading = true;
    this.BtError = '';
    this.BtInfo = null;
    try {
      this.BtInfo = await lastValueFrom(this.paymentsData.getBraintreeTransaction(id));
    } catch (err: any) {
      this.BtError = err?.error?.error ?? 'Could not read Braintree.';
    } finally {
      this.BtLoading = false;
      this.cdr.markForCheck();
    }
  }

  stageClass(stage: string | null | undefined): string {
    return `stage stage-${String(stage ?? '').toLowerCase()}`;
  }

  close() {
    this.ref.close(this.Changed);
  }
}
